import React from 'react'
import styled from '@emotion/styled'
import DOMPurify from 'isomorphic-dompurify'
import { stateToHTML } from 'draft-js-export-html'
import { convertFromRaw } from 'draft-js'
import { CloudinaryAdvancedImage } from '../Misc/CloudinaryImage'
import { FullWidth } from '../Misc/FullWidth'
import { draftJsOptionsObject, getContrastSafeColor } from '../Misc/utils'
import { storyTypography } from '../../../tokens/typography-storybuilder'

export const ImageAndTextBlockPublic = ({
  asset,
  backgroundColor,
  cloudName,
}: {
  asset: {
    id: number
    assetDetails: {
      blocks: any[]
      entityMap: any
      cloudinaryFileId: string | null
      imagePosition: string
    }
  }
  backgroundColor?: string
  cloudName?: string
}): JSX.Element | null => {
  const getOrientation = () => {
    if (
      asset.assetDetails?.imagePosition === 'LEFT' ||
      asset.assetDetails?.imagePosition === 'RIGHT'
    ) {
      return 'HALF'
    }
    if (asset.assetDetails?.imagePosition === 'BOTTOM') {
      return 'FULL'
    }
    return 'HALF'
  }

  const textDetails = {
    blocks: asset.assetDetails?.blocks,
    entityMap: asset.assetDetails?.entityMap,
  }

  return (
    <FullWidth backgroundColor={backgroundColor}>
      <BlockContainer
        imagePosition={asset.assetDetails.imagePosition}
        cloudinaryFileId={asset.assetDetails?.cloudinaryFileId}
        hasBlockValue={asset.assetDetails?.blocks[0]}
      >
        {asset.assetDetails?.blocks[0] && (
          <TextContainer
            orientation={getOrientation()}
            cloudinaryFileId={asset.assetDetails?.cloudinaryFileId}
            backgroundColor={backgroundColor || '#FFFFFF'}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  stateToHTML(convertFromRaw(textDetails), draftJsOptionsObject)
                ),
              }}
            />
          </TextContainer>
        )}
        {asset.assetDetails?.cloudinaryFileId && (
          <CloudinaryAdvancedImage
            cloudName={cloudName}
            imagePublicId={asset.assetDetails.cloudinaryFileId}
            style={{
              backgroundRepeat: 'no-repeat',
              maxWidth: 'min(750px, 100vw - 48px)',
              width:
                getOrientation() === 'HALF' && asset.assetDetails?.blocks[0]
                  ? '505px'
                  : '672px',
              display: 'block',
              borderRadius: '6px',
            }}
            imageAltText=''
          />
        )}
      </BlockContainer>
    </FullWidth>
  )
}

const getFlexDirection = (imagePosition: string) => {
  switch (imagePosition) {
    case 'TOP':
      return 'column-reverse'
    case 'BOTTOM':
      return 'column'
    case 'LEFT':
      return 'row-reverse'
    case 'RIGHT':
      return 'row'
    default:
      return 'column'
  }
}

const TextContainer = styled('div')<{
  orientation: string
  cloudinaryFileId: string | null
  backgroundColor: string
}>`
  max-width: ${({ orientation, cloudinaryFileId }) =>
    orientation === 'HALF' && cloudinaryFileId ? '505px' : '672px'};
  flex: 1;
  width: 100%;
  ${storyTypography.freeTextBlock.styles};
  & a {
    color: ${({ backgroundColor }) => getContrastSafeColor(backgroundColor)};
  }
`

const BlockContainer = styled('div')<{
  imagePosition: string
  cloudinaryFileId: string | null
  hasBlockValue: boolean
}>`
  display: flex;
  flex-direction: ${({ imagePosition }) => getFlexDirection(imagePosition)};
  max-width: 1058px;
  margin: 0 auto;
  align-items: center;
  justify-content: ${({ cloudinaryFileId, hasBlockValue }) =>
    cloudinaryFileId && hasBlockValue ? 'space-between' : 'center'};
  @media screen and (max-width: 1106px) {
    flex-direction: column;
  }
  @media print {
    gap: 24px;
    img {
      ${({ imagePosition }) => {
        if (imagePosition === 'LEFT' || imagePosition === 'RIGHT') {
          return 'width: 258px !important; flex: 1'
        }
        return ''
      }}
    }
  }
`
